import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import NotFound from "./pages/NotFound";

// ---- Hardcoded target (no env needed) ----
const BACK_LINK = "https://www.monicaisystems.com/services/monic-automate/";

// Lazy load components to reduce initial bundle size
const CryoLeadCalculator = lazy(() => import("@/components/CryoLeadCalculator"));
const PasswordProtectedHVAC = lazy(() => import("./components/PasswordProtectedHVAC"));

const queryClient = new QueryClient();

/**
 * Rewrites any anchor pointing to /agents (absolute or relative) to BACK_LINK.
 * Runs on first render and on every route change.
 */
function LinkFixer() {
  const location = useLocation();

  useEffect(() => {
    const AGENTS_RE = /(^\/agents\/?$)|monicaisystems\.com\/agents\/?/i;

    const anchors = Array.from(document.querySelectorAll<HTMLAnchorElement>("a[href]"));
    anchors.forEach((a) => {
      const href = a.getAttribute("href") || "";
      if (AGENTS_RE.test(href)) {
        a.setAttribute("href", BACK_LINK);
        a.setAttribute("target", "_self"); // change to "_blank" if you want a new tab
        a.setAttribute("rel", "noopener noreferrer");
        if (a.textContent && /agents/i.test(a.textContent)) {
          a.textContent = a.textContent.replace(/agents/gi, "Monic Automate");
        }
      }
    });
  }, [location]);

  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        {/* Auto-fix legacy /agents hrefs after each render/navigation */}
        <LinkFixer />

        <Routes>
          <Route
            path="/"
            element={
              <Suspense
                fallback={
                  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-accent/5 to-primary/5">
                    Loading...
                  </div>
                }
              >
                <CryoLeadCalculator />
              </Suspense>
            }
          />
          <Route
            path="/hvac"
            element={
              <Suspense
                fallback={
                  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-accent/5 to-primary/5">
                    Loading...
                  </div>
                }
              >
                <PasswordProtectedHVAC />
              </Suspense>
            }
          />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
